import {Badge, FieldInput, FieldSelect, InfoTooltip} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ErrorAlertMessage} from '@/components';
import {generateTrustAccountName, withDebugFieldChange} from '@/features/debug-test-data';

import {TRUST_PURPOSE_CONFIG} from '../config';

import type {TrustPurposeConfig} from '../config';
import type {TrustPurpose} from '@sollapay/enums';
import type {FC, ReactNode} from 'react';

type TrustAccountFormFieldsProps = {
  name: string;
  onNameChange: (value: string) => void;
  trustPurpose: string;
  onTrustPurposeChange: (value: string) => void;
  submitError?: unknown;
  showPurposeField?: boolean;
};

export const TrustAccountFormFields: FC<TrustAccountFormFieldsProps> = ({
  name,
  onNameChange,
  trustPurpose,
  onTrustPurposeChange,
  submitError,
  showPurposeField = true
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);

  const trustPurposeOptions = useMemo(
    () =>
      (Object.entries(TRUST_PURPOSE_CONFIG) as Array<[TrustPurpose, TrustPurposeConfig]>)
        .filter(([, {hidden}]) => !hidden)
        .map(([value, {labelKey, comingSoon}]) => ({
          value,
          disabled: comingSoon,
          label: t(labelKey),
          badge: comingSoon ? (
            <Badge size="sm" variant="neutral">
              {t('common:comingSoon')}
            </Badge>
          ) : undefined
        })),
    [t]
  );

  const purposeLabel: ReactNode = (
    <span className="flex items-center gap-1">
      {t('modal.purposeLabel')}
      <InfoTooltip content={t('modal.purposeTooltip')} />
    </span>
  );

  return (
    <div className="flex flex-col gap-6">
      {submitError != null && (
        <ErrorAlertMessage
          error={submitError}
          trustPurpose={(trustPurpose as TrustPurpose) || null}
        />
      )}

      <FieldInput
        label={t('modal.nameLabel')}
        value={name}
        onChange={event => onNameChange(event.target.value)}
        placeholder={t('modal.namePlaceholder')}
        cornerBadge={withDebugFieldChange(undefined, onNameChange, generateTrustAccountName)}
      />

      {showPurposeField && (
        <FieldSelect
          label={purposeLabel}
          value={trustPurpose}
          onValueChange={onTrustPurposeChange}
          placeholder={t('modal.selectPlaceholder')}
          options={trustPurposeOptions}
        />
      )}
    </div>
  );
};
